/**
 * Lecture d'un fichier ZIP directement dans le navigateur, sans bibliothèque.
 *
 * Le fichier n'est jamais chargé en entier en mémoire : on lit le répertoire
 * central (à la fin du fichier), puis chaque entrée seulement quand on en a
 * besoin. Une archive Procore de plusieurs centaines de mégaoctets s'ouvre
 * donc sans problème. La décompression utilise le moteur natif du navigateur
 * (DecompressionStream, Chrome et Edge récents, Safari 16.4+).
 */

export interface EntreeZip {
  nom: string
  taille: number
  tailleCompressee: number
  methode: number
  decalageLocal: number
}

const SIG_FIN = 0x06054b50
const SIG_FIN64 = 0x06064b50
const SIG_LOCAL64 = 0x07064b50
const SIG_CENTRAL = 0x02014b50
const PLEIN = 0xffffffff

async function lire(f: Blob, debut: number, fin: number): Promise<DataView> {
  return new DataView(await f.slice(debut, fin).arrayBuffer())
}

function u64(v: DataView, o: number): number {
  return v.getUint32(o, true) + v.getUint32(o + 4, true) * 2 ** 32
}

export async function lireZip(f: Blob): Promise<EntreeZip[]> {
  // 1. Fin du répertoire central : dans les derniers 64 Ko (+ 22 octets).
  const queueDebut = Math.max(0, f.size - 65557)
  const queue = await lire(f, queueDebut, f.size)
  let fin = -1
  for (let i = queue.byteLength - 22; i >= 0; i--) {
    if (queue.getUint32(i, true) === SIG_FIN) {
      fin = i
      break
    }
  }
  if (fin < 0) throw new Error("Ce fichier n'est pas une archive ZIP valide (ou il est incomplet).")

  let nbEntrees = queue.getUint16(fin + 10, true)
  let tailleRep = queue.getUint32(fin + 12, true)
  let debutRep = queue.getUint32(fin + 16, true)

  // ZIP64 : grosses archives (plus de 65 535 fichiers ou plus de 4 Go).
  if (nbEntrees === 0xffff || tailleRep === PLEIN || debutRep === PLEIN) {
    const loc = fin - 20
    if (loc >= 0 && queue.getUint32(loc, true) === SIG_LOCAL64) {
      const pos64 = u64(queue, loc + 8)
      const v = await lire(f, pos64, pos64 + 56)
      if (v.getUint32(0, true) !== SIG_FIN64) throw new Error('Archive ZIP64 illisible.')
      nbEntrees = u64(v, 32)
      tailleRep = u64(v, 40)
      debutRep = u64(v, 48)
    }
  }

  // 2. Répertoire central.
  const rep = await lire(f, debutRep, debutRep + tailleRep)
  const utf8 = new TextDecoder('utf-8')
  const entrees: EntreeZip[] = []
  let p = 0
  for (let n = 0; n < nbEntrees && p + 46 <= rep.byteLength; n++) {
    if (rep.getUint32(p, true) !== SIG_CENTRAL) break
    const methode = rep.getUint16(p + 10, true)
    let tailleCompressee = rep.getUint32(p + 20, true)
    let taille = rep.getUint32(p + 24, true)
    const lNom = rep.getUint16(p + 28, true)
    const lExtra = rep.getUint16(p + 30, true)
    const lComm = rep.getUint16(p + 32, true)
    let decalageLocal = rep.getUint32(p + 42, true)
    const nom = utf8.decode(new Uint8Array(rep.buffer, rep.byteOffset + p + 46, lNom))

    // Champ supplémentaire ZIP64 : tailles et position sur 8 octets.
    let e = p + 46 + lNom
    const finExtra = e + lExtra
    while (e + 4 <= finExtra) {
      const id = rep.getUint16(e, true)
      const lg = rep.getUint16(e + 2, true)
      if (id === 0x0001) {
        let q = e + 4
        if (taille === PLEIN) { taille = u64(rep, q); q += 8 }
        if (tailleCompressee === PLEIN) { tailleCompressee = u64(rep, q); q += 8 }
        if (decalageLocal === PLEIN) { decalageLocal = u64(rep, q) }
      }
      e += 4 + lg
    }

    if (!nom.endsWith('/')) entrees.push({ nom, taille, tailleCompressee, methode, decalageLocal })
    p = finExtra + lComm
  }
  return entrees
}

/** Extrait une entrée et la rend sous forme de Blob. */
export async function extraire(f: Blob, e: EntreeZip, type = ''): Promise<Blob> {
  const tete = await lire(f, e.decalageLocal, e.decalageLocal + 30)
  const debut = e.decalageLocal + 30 + tete.getUint16(26, true) + tete.getUint16(28, true)
  const brut = f.slice(debut, debut + e.tailleCompressee)
  if (e.methode === 0) return new Blob([brut], { type })
  if (e.methode !== 8) throw new Error(`Méthode de compression non prise en charge (${e.methode}).`)
  const flux = brut.stream().pipeThrough(new DecompressionStream('deflate-raw'))
  const blob = await new Response(flux).blob()
  return type ? new Blob([blob], { type }) : blob
}
